/**
 * Dashboard API Client
 *
 * Calls the same-origin Railway server (Express). Auth flows through the
 * browser's Clerk session cookie — no API key needed from the client side.
 */

// Same-origin: routes mount under /api on the Railway server.
const API_URL = '/api';

// Types
export interface SystemMetrics {
  activeBatches: number;
  pendingDocuments: number;
  indexedToday: number;
  successRate: number;
  currentThroughput: number;
  systemStatus: 'healthy' | 'degraded' | 'error';
  entityBreakdown?: Record<string, number>;
}

export interface DocumentCounts {
  queued: number;
  compiled: number;
  batched: number;
  ready_for_index: number;
  indexed: number;
  failed: number;
}

export interface Counters {
  person: number;
  company: number;
  opportunity?: number;
  note?: number;
  total: number;
  [key: string]: number | undefined;
}

export interface BatchJob {
  batchId: string;
  entityType: 'person' | 'company' | 'opportunity' | 'note';
  status: 'created' | 'in_progress' | 'ended' | 'failed' | 'expired';
  documentCount: number;
  createdAt: number;
  updatedAt: number;
  completedAt?: number;
}

export interface BatchFilters {
  entityType?: 'person' | 'company' | 'opportunity' | 'note';
  status?: 'created' | 'in_progress' | 'ended' | 'failed' | 'expired';
  limit?: number;
}

export interface SweeperPerAlias {
  alias: string;
  vectorField: string;
  inspected: number;
  broken: number;
  enqueued: number;
  skipped: boolean;
}

export interface SweeperRun {
  id: string;
  ranAt: number;
  durationMs: number;
  totalInspected: number;
  totalBroken: number;
  totalEnqueued: number;
  perAlias: SweeperPerAlias[];
}

export interface SweeperCursor {
  alias: string;
  vectorField: string;
  lastSweepAt: number | null;
  lastInspected: number;
  lastBrokenFound: number;
}

export interface SweeperMetrics {
  runs: SweeperRun[];
  summary: {
    runsLast30d: number;
    inspectedLast30d: number;
    brokenLast30d: number;
    enqueuedLast30d: number;
    lastBrokenAt: number | null;
  };
  cursors: SweeperCursor[];
  fetchedAt: string;
}

/**
 * Base fetch wrapper with API key authentication
 */
async function apiFetch<T>(endpoint: string): Promise<T> {
  const url = `${API_URL}${endpoint}`;

  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`API Error (${response.status}): ${error}`);
  }

  return response.json();
}

/**
 * Get system-wide metrics
 * @param entityType - Optional entity type filter ('person', 'company', 'opportunity', 'note', or undefined for all)
 */
export async function getMetrics(entityType?: 'person' | 'company' | 'opportunity' | 'note'): Promise<SystemMetrics> {
  const params = new URLSearchParams();
  if (entityType) {
    params.append('entityType', entityType);
  }

  const query = params.toString();
  const endpoint = `/dashboard/metrics${query ? `?${query}` : ''}`;

  return apiFetch<SystemMetrics>(endpoint);
}

/**
 * Get vector-quality-sweeper run history + 30-day rollup + per-entity cursor state
 */
export async function getSweeperMetrics(limit = 30): Promise<SweeperMetrics> {
  return apiFetch<SweeperMetrics>(`/dashboard/sweeper?limit=${limit}`);
}

/**
 * Get document counts by status
 */
export async function getDocumentCounts(): Promise<DocumentCounts> {
  return apiFetch<DocumentCounts>('/dashboard/documents/counts');
}

/**
 * Get pending counters by entity type
 */
export async function getCounters(): Promise<Counters> {
  return apiFetch<Counters>('/dashboard/counters');
}

/**
 * Get batch jobs with optional filters
 */
export async function getBatchJobs(filters?: BatchFilters): Promise<BatchJob[]> {
  const params = new URLSearchParams();

  if (filters?.entityType) {
    params.append('entityType', filters.entityType);
  }
  if (filters?.status) {
    params.append('status', filters.status);
  }
  if (filters?.limit) {
    params.append('limit', filters.limit.toString());
  }

  const query = params.toString();
  const endpoint = `/dashboard/batches${query ? `?${query}` : ''}`;

  return apiFetch<BatchJob[]>(endpoint);
}

/**
 * Get single batch details with documents
 */
export async function getBatchDetails(batchId: string): Promise<{
  batch: BatchJob;
  documents: any[];
}> {
  return apiFetch(`/dashboard/batch/${batchId}`);
}

// Extend BatchJob interface with Anthropic metadata
declare module './dashboard-api' {
  interface BatchJob {
    triggerReason?: 'threshold' | 'scheduled';
    requestCounts?: {
      processing: number;
      canceled: number;
      expired: number;
      succeeded: number;
      errored: number;
    };
  }
}

/**
 * Get failing messages with retry details
 */
export async function getFailingMessages(options?: {
  limit?: number;
  offset?: number;
  entityType?: 'person' | 'company' | 'opportunity' | 'note';
  status?: string;
}): Promise<any> {
  const params = new URLSearchParams();

  if (options?.limit) {
    params.append('limit', options.limit.toString());
  }
  if (options?.offset) {
    params.append('offset', options.offset.toString());
  }
  if (options?.entityType) {
    params.append('entityType', options.entityType);
  }
  if (options?.status) {
    params.append('status', options.status);
  }

  const query = params.toString();
  const endpoint = `/dashboard/failing-messages${query ? `?${query}` : ''}`;

  return apiFetch(endpoint);
}
